import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Menu from "@material-ui/core/Menu";
import Popover from "@material-ui/core/Popover";
import Block from "@material-ui/icons/Block";
import MenuItem from "@material-ui/core/MenuItem";
import Search from "@material-ui/icons/Search";
import Add from "@material-ui/icons/Add";
import EditOutlined from "@material-ui/icons/EditOutlined";
import FileCopyOutlined from "@material-ui/icons/FileCopyOutlined";
import MoreVert from "@material-ui/icons/MoreVert";
import FolderOpen from "@material-ui/icons/FolderOpen";
import InsertDriveFileOutlined from "@material-ui/icons/InsertDriveFileOutlined";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import PlayCircleOutline from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutline from "@material-ui/icons/PauseCircleOutline";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import UnfoldMore from "@material-ui/icons/UnfoldMore";
import FarApi from "../../../../assets/js/utils/far/farApi";
import { getRuleType, getOpLabel, isRuleWebEvaluable, ruleHasExclusions, describeExclusionCounts, FAR_EXCLUSION, FAR_EXCLUSION_LABEL } from "../../../../assets/js/utils/far/farEngine";
import farPageStyle from "../../../../assets/jss/user/far/farPageStyle.jsx";
import { farShared } from "../../../../assets/jss/user/far/farTokens.jsx";
import { FarPill, FarNotice, formatFarDate } from "./farUi";
import FarRuleModal from "./FarRuleModal";
import FarConfirmModal from "./FarConfirmModal";
import LoaderComponent from "../../Loader";

const useStyles = makeStyles(theme => {
    const base = farPageStyle(theme);
    const s = farShared(theme);
    const t = s.t;
    return Object.assign(base, {
        blockedNone: { fontSize: "13px", color: t.textMuted },
        blockedButton: {
            ...s.focusRing,
            display: "inline-flex", alignItems: "center", gap: "4px", border: "none", background: "transparent", padding: "2px 0",
            color: t.danger.fg, fontWeight: 700, fontSize: "13px", cursor: "pointer", whiteSpace: "nowrap", textDecoration: "underline dotted",
            "& svg": { width: "16px", height: "16px" }
        },
        blockedPaper: {
            backgroundColor: t.dark ? "#1A2238" : "#FFFFFF", color: t.text, border: "1px solid "+t.border, borderRadius: "10px",
            padding: "14px 16px", width: "320px", maxWidth: "calc(100vw - 32px)", maxHeight: "360px", overflowY: "auto", textAlign: "left"
        },
        blockedTitle: { margin: "0 0 2px", fontSize: "15px", fontWeight: 700, color: t.textStrong },
        blockedAction: { margin: "0 0 10px", fontSize: "12px", color: t.textMuted },
        blockedHeading: { margin: "10px 0 4px", fontSize: "12px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: t.textMuted },
        blockedList: { margin: 0, padding: 0, listStyle: "none", fontSize: "13px", "& li": { padding: "2px 0", wordBreak: "break-all", color: t.danger.fg } },
        blockedFoot: { margin: "12px 0 0", fontSize: "12px", color: t.textMuted, lineHeight: "17px" }
    });
});

/** "Blocked Recipients" cell: counts, with an accessible popover listing the entries. */
function BlockedRecipients({ rule, classes }){
    const [anchor, setAnchor] = React.useState(null);
    if(!ruleHasExclusions(rule)){
        return <span className={classes.blockedNone}>None</span>;
    }
    const popoverId = "far-blocked-"+rule.id;
    const titleId = popoverId+"-title";
    const action = rule.exclusionAction || FAR_EXCLUSION.BLOCK;
    return (
        <>
            <button type="button" className={classes.blockedButton} aria-haspopup="dialog" aria-expanded={anchor ? "true" : "false"} aria-controls={anchor ? popoverId : undefined}
                aria-label={"Blocked recipients for "+rule.name+": "+describeExclusionCounts(rule)} onClick={(e) => setAnchor(e.currentTarget)}>
                <Block aria-hidden="true" />{describeExclusionCounts(rule)}
            </button>
            <Popover id={popoverId} open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} classes={{ paper: classes.blockedPaper }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }} transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ role: "dialog", "aria-labelledby": titleId }}>
                <p className={classes.blockedTitle} id={titleId}>Blocked recipients</p>
                <p className={classes.blockedAction}>{FAR_EXCLUSION_LABEL[action] || FAR_EXCLUSION_LABEL[FAR_EXCLUSION.BLOCK]}</p>
                {
                    rule.blockedListsVisible ?
                        <>
                            { rule.blockedEmails.length > 0 ? <p className={classes.blockedHeading}>Email addresses ({rule.blockedEmails.length})</p> : null }
                            { rule.blockedEmails.length > 0 ? <ul className={classes.blockedList}>{ rule.blockedEmails.map(email => <li key={email}>{email}</li>) }</ul> : null }
                            { rule.blockedDomains.length > 0 ? <p className={classes.blockedHeading}>Domains ({rule.blockedDomains.length})</p> : null }
                            { rule.blockedDomains.length > 0 ? <ul className={classes.blockedList}>{ rule.blockedDomains.map(domain => <li key={domain}>{domain}</li>) }</ul> : null }
                        </>
                    :
                        <p className={classes.blockedFoot}>{describeExclusionCounts(rule)}. Only the rule's owner or an administrator can see the blocked list.</p>
                }
                <p className={classes.blockedFoot}>Blocked recipients take precedence over authorized recipients.</p>
            </Popover>
        </>
    );
}

/*
 * Name conditions are joined by their operator. The two folder conditions are
 * alternatives for different channels (Sendlinx folder = web, local folder =
 * Outlook plug-in), so no operator is shown between them.
 */
function RuleConditions({ rule, classes }){
    const lines = [];
    if(rule.prefix){ lines.push({ key: "prefix", label: "Prefix: "+rule.prefix, op: rule.prefixOp }); }
    if(rule.suffix){ lines.push({ key: "suffix", label: "Suffix: "+rule.suffix, op: rule.suffixOp }); }
    if(rule.sendlinxFolder){ lines.push({ key: "sendlinx", label: rule.sendlinxFolder, op: null }); }
    if(rule.directory){ lines.push({ key: "directory", label: rule.directory, op: null }); }
    return (
        <ul className={classes.conditions}>
            {
                lines.map((line, index) => (
                    <React.Fragment key={line.key}>
                        <li className={classes.condition}>
                            {
                                line.key === "directory" || line.key === "sendlinx" ?
                                    <FolderOpen className={classes.folderIcon} aria-label={line.key === "sendlinx" ? "Sendlinx folder" : "Local folder"} />
                                :
                                    <InsertDriveFileOutlined className={classes.fileIcon} aria-hidden="true" />
                            }
                            <span>
                                {line.label}
                                {
                                    line.key === "sendlinx" ?
                                        <span className={classes.webChip} title="A folder in Sendlinx My Docs. Checked on the web portal when files are sent from My Docs, including subfolders.">My Docs · web</span>
                                    : line.key === "directory" ?
                                        <span className={classes.outlookOnly} title="A folder on the sender's computer. Browsers cannot read local folder paths, so the Outlook plug-in checks this condition and the web portal skips it.">Local · Outlook plug-in</span>
                                    : null
                                }
                            </span>
                        </li>
                        { index < lines.length - 1 && line.op !== null ? <li className={classes.op} aria-label={"Operator "+getOpLabel(line.op)}>{getOpLabel(line.op)}</li> : null }
                    </React.Fragment>
                ))
            }
        </ul>
    );
}

function FarRulesTab({ rules, loading, loadError, canManage, onRulesChanged, onRetry }){
    const classes = useStyles();
    const [search, setSearch] = React.useState("");
    const [status, setStatus] = React.useState("all");
    const [type, setType] = React.useState("all");
    const [restriction, setRestriction] = React.useState("all");
    const [sortDir, setSortDir] = React.useState(null);
    const [page, setPage] = React.useState(1);
    const [perPage, setPerPage] = React.useState(25);
    const [selected, setSelected] = React.useState([]);
    const [modal, setModal] = React.useState(null); // { mode, rule }
    const [confirm, setConfirm] = React.useState(null); // { ids, action }
    const [busy, setBusy] = React.useState(false);
    const [notice, setNotice] = React.useState(null);
    const [menu, setMenu] = React.useState(null); // { anchor, rule }

    const filtered = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        let list = (rules || []).filter(rule => {
            if(status === "active" && !rule.active){ return false; }
            if(status === "inactive" && rule.active){ return false; }
            if(type !== "all" && getRuleType(rule) !== type){ return false; }
            if(restriction === "with" && !ruleHasExclusions(rule)){ return false; }
            if(restriction === "without" && ruleHasExclusions(rule)){ return false; }
            if(restriction === "hard" && !(ruleHasExclusions(rule) && rule.exclusionAction === FAR_EXCLUSION.BLOCK)){ return false; }
            if(q.length > 0){
                const haystack = [rule.name, rule.description, rule.prefix, rule.suffix, rule.directory, rule.sendlinxFolder, rule.emails.join(" "),
                    (rule.blockedEmails || []).join(" "), (rule.blockedDomains || []).join(" ")].join(" ").toLowerCase();
                return haystack.indexOf(q) >= 0;
            }
            return true;
        });
        if(sortDir){
            list = list.slice().sort((a, b) => a.name.localeCompare(b.name) * (sortDir === "asc" ? 1 : -1));
        }
        return list;
    }, [rules, search, status, type, restriction, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const currentPage = Math.min(page, totalPages);
    const pageRows = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
    const pageIds = pageRows.map(r => r.id);
    const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => selected.includes(id));

    React.useEffect(() => { setPage(1); }, [search, status, type, restriction, perPage]);
    React.useEffect(() => {
        // Drop selections for rules that no longer exist.
        const ids = (rules || []).map(r => r.id);
        setSelected(prev => prev.filter(id => ids.includes(id)));
    }, [rules]);

    const toggle = (id) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : prev.concat([id])));
    const toggleAll = () => setSelected(prev => (allOnPageSelected ? prev.filter(id => !pageIds.includes(id)) : Array.from(new Set(prev.concat(pageIds)))));

    const runBulk = (ids, action) => {
        setBusy(true);
        setNotice(null);
        const done = () => {
            setBusy(false);
            setConfirm(null);
            setSelected(prev => prev.filter(id => !ids.includes(id)));
            const verb = action === "delete" ? "deleted" : (action === "activate" ? "activated" : "deactivated");
            setNotice({ tone: "success", text: ids.length+" rule"+(ids.length > 1 ? "s" : "")+" "+verb+"." });
            onRulesChanged();
        };
        const fail = (err) => {
            setBusy(false);
            setConfirm(null);
            setNotice({ tone: "danger", text: (err && err.message) || "The change could not be saved. Nothing was changed." });
        };
        if(action === "delete" && ids.length === 1){
            FarApi.deleteRule(ids[0]).then(done).catch(fail);
            return;
        }
        FarApi.bulkRules(ids, action).then(done).catch(fail);
    };

    const onSaved = (saved) => {
        const mode = modal ? modal.mode : "add";
        setModal(null);
        setNotice({ tone: "success", text: "Rule \""+(saved && saved.name ? saved.name : "")+"\" "+(mode === "edit" ? "updated." : "created.") });
        onRulesChanged();
    };

    const openMenu = (e, rule) => setMenu({ anchor: e.currentTarget, rule });
    const closeMenu = () => setMenu(null);

    return (
        <div className={classes.main}>
            <div className={classes.toolbar}>
                <div className={classes.searchWrap}>
                    <Search aria-hidden="true" />
                    <label htmlFor="far-rules-search" className={classes.srOnly}>Search rules</label>
                    <input id="far-rules-search" type="search" className={classes.search} placeholder="Search rules by name, folder, email, blocked domain or keyword…"
                        value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <label htmlFor="far-rules-status" className={classes.srOnly}>Filter by status</label>
                <select id="far-rules-status" className={classes.select} value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
                <label htmlFor="far-rules-type" className={classes.srOnly}>Filter by rule type</label>
                <select id="far-rules-type" className={classes.select} value={type} onChange={(e) => setType(e.target.value)}>
                    <option value="all">All Rule Types</option>
                    <option value="prefix">Prefix</option>
                    <option value="suffix">Suffix</option>
                    <option value="folder">Sendlinx folder</option>
                    <option value="directory">Local folder (Outlook)</option>
                    <option value="combined">Combined</option>
                </select>
                <label htmlFor="far-rules-restriction" className={classes.srOnly}>Filter by recipient restrictions</label>
                <select id="far-rules-restriction" className={classes.select} value={restriction} onChange={(e) => setRestriction(e.target.value)}>
                    <option value="all">All Recipient Restrictions</option>
                    <option value="with">With exclusions</option>
                    <option value="hard">Hard-block exclusions</option>
                    <option value="without">Without exclusions</option>
                </select>
                {
                    canManage ?
                        <button type="button" className={classes.addButton} onClick={() => setModal({ mode: "add", rule: null })}>
                            <Add aria-hidden="true" />Add Rule
                        </button>
                    : null
                }
            </div>

            { notice ? <FarNotice tone={notice.tone} action={<button type="button" className={classes.iconButton} aria-label="Dismiss" onClick={() => setNotice(null)}>×</button>}>{notice.text}</FarNotice> : null }
            { !canManage ? <FarNotice tone="info">Rules are managed by your account administrator. You can view them here.</FarNotice> : null }

            {
                canManage && selected.length > 0 ?
                    <div className={classes.bulkBar} role="region" aria-label="Bulk actions">
                        <span>{selected.length} selected</span>
                        <button type="button" className={classes.bulkButton} onClick={() => runBulk(selected, "activate")} disabled={busy}>Activate</button>
                        <button type="button" className={classes.bulkButton} onClick={() => runBulk(selected, "deactivate")} disabled={busy}>Deactivate</button>
                        <button type="button" className={classes.bulkDanger} onClick={() => setConfirm({ ids: selected.slice(), action: "delete" })} disabled={busy}>Delete</button>
                        <button type="button" className={classes.bulkButton} onClick={() => setSelected([])} disabled={busy}>Clear</button>
                    </div>
                : null
            }

            <div className={classes.tableCard}>
                <div className={classes.tableScroll}>
                    <table className={classes.table}>
                        <caption className={classes.srOnly}>File Attachment Rules</caption>
                        <thead>
                            <tr>
                                {
                                    canManage ?
                                        <th className={classes.checkCell} scope="col">
                                            <input type="checkbox" aria-label="Select all rules on this page" checked={allOnPageSelected} onChange={toggleAll} disabled={pageIds.length <= 0} />
                                        </th>
                                    : null
                                }
                                <th scope="col">Status</th>
                                <th scope="col" aria-sort={sortDir === "asc" ? "ascending" : (sortDir === "desc" ? "descending" : "none")}>
                                    <button type="button" className={classes.sortButton} onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}>
                                        Rule Name <UnfoldMore style={{width: 16, height: 16}} aria-hidden="true" />
                                    </button>
                                </th>
                                <th scope="col">Conditions (File Match)</th>
                                <th scope="col">Authorized Recipients</th>
                                <th scope="col">Blocked Recipients</th>
                                <th scope="col">Last Modified</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                loading ?
                                    <tr><td colSpan={8} className={classes.empty}><LoaderComponent color="custom" align="center" /></td></tr>
                                : loadError ?
                                    <tr><td colSpan={8} className={classes.empty}>
                                        <FarNotice tone="danger" action={<button type="button" className={classes.bulkButton} onClick={onRetry}>Retry</button>}>{loadError}</FarNotice>
                                    </td></tr>
                                : pageRows.length <= 0 ?
                                    <tr><td colSpan={8} className={classes.empty}>
                                        { (rules || []).length <= 0 ? "No rules yet. Add a rule to start checking attachments." : "No rules match your search or filters." }
                                    </td></tr>
                                :
                                    pageRows.map(rule => (
                                        <tr key={rule.id} aria-selected={selected.includes(rule.id) ? "true" : "false"}>
                                            {
                                                canManage ?
                                                    <td className={classes.checkCell}>
                                                        <input type="checkbox" aria-label={"Select "+rule.name} checked={selected.includes(rule.id)} onChange={() => toggle(rule.id)} />
                                                    </td>
                                                : null
                                            }
                                            <td>
                                                <FarPill tone={rule.active ? "success" : "neutral"}>{rule.active ? "Active" : "Inactive"}</FarPill>
                                            </td>
                                            <td>
                                                <p className={classes.ruleName}>{rule.name}</p>
                                                { rule.description ? <p className={classes.ruleDesc}>{rule.description}</p> : null }
                                                { rule.active && !isRuleWebEvaluable(rule) ? <p className={classes.ruleDesc}>Outlook plug-in only</p> : null }
                                            </td>
                                            <td><RuleConditions rule={rule} classes={classes} /></td>
                                            <td>
                                                <div className={classes.emails}>
                                                    { rule.emails.slice(0, 3).map(email => <span key={email}>{email}</span>) }
                                                    { rule.emails.length > 3 ? <span title={rule.emails.slice(3).join(", ")}>+{rule.emails.length - 3} more</span> : null }
                                                </div>
                                            </td>
                                            <td><BlockedRecipients rule={rule} classes={classes} /></td>
                                            <td className={classes.modified}>
                                                {formatFarDate(rule.updatedAt) || "—"}
                                                { rule.updatedBy ? <span>{rule.updatedBy}</span> : null }
                                            </td>
                                            <td>
                                                {
                                                    canManage ?
                                                        <div className={classes.actions}>
                                                            <button type="button" className={classes.iconButton} aria-label={"Edit "+rule.name} title="Edit" onClick={() => setModal({ mode: "edit", rule })}><EditOutlined /></button>
                                                            <button type="button" className={classes.iconButton} aria-label={"Duplicate "+rule.name} title="Duplicate"
                                                                onClick={() => setModal({ mode: "duplicate", rule: Object.assign({}, rule, { id: null, name: rule.name+" (copy)" }) })}><FileCopyOutlined /></button>
                                                            <button type="button" className={classes.iconButton} aria-label={"More actions for "+rule.name} aria-haspopup="true" title="More" onClick={(e) => openMenu(e, rule)}><MoreVert /></button>
                                                        </div>
                                                    : <span className={classes.ruleDesc}>View only</span>
                                                }
                                            </td>
                                        </tr>
                                    ))
                            }
                        </tbody>
                    </table>
                </div>
            </div>

            <div className={classes.footer}>
                <span role="status">
                    {
                        filtered.length <= 0 ? "Showing 0 rules" :
                        "Showing "+((currentPage - 1) * perPage + 1)+" to "+Math.min(currentPage * perPage, filtered.length)+" of "+filtered.length+" rule"+(filtered.length > 1 ? "s" : "")
                    }
                </span>
                <div className={classes.pager}>
                    <button type="button" className={classes.pageButton} aria-label="Previous page" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}><ChevronLeft /></button>
                    {
                        Array.from({ length: totalPages }, (v, i) => i + 1).filter(n => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1).map(n => (
                            <button type="button" key={n} className={classes.pageButton+" "+(n === currentPage ? classes.pageActive : "")}
                                aria-label={"Page "+n} aria-current={n === currentPage ? "page" : undefined} onClick={() => setPage(n)}>{n}</button>
                        ))
                    }
                    <button type="button" className={classes.pageButton} aria-label="Next page" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}><ChevronRight /></button>
                    <label htmlFor="far-rules-per-page">Rows per page</label>
                    <select id="far-rules-per-page" className={classes.rowsSelect} value={perPage} onChange={(e) => setPerPage(parseInt(e.target.value, 10))}>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
            </div>

            <Menu anchorEl={menu ? menu.anchor : null} open={!!menu} onClose={closeMenu} classes={{ paper: classes.menuPaper }} keepMounted={false}>
                {
                    menu ?
                        [
                            <MenuItem key="toggle" onClick={() => { const r = menu.rule; closeMenu(); runBulk([r.id], r.active ? "deactivate" : "activate"); }}>
                                { menu.rule.active ? <PauseCircleOutline aria-hidden="true" /> : <PlayCircleOutline aria-hidden="true" /> }
                                { menu.rule.active ? "Deactivate" : "Activate" }
                            </MenuItem>,
                            <MenuItem key="delete" className={classes.menuDanger} onClick={() => { const r = menu.rule; closeMenu(); setConfirm({ ids: [r.id], action: "delete", name: r.name }); }}>
                                <DeleteOutline aria-hidden="true" />Delete
                            </MenuItem>
                        ]
                    : []
                }
            </Menu>

            {
                modal ?
                    <FarRuleModal open={true} mode={modal.mode} rule={modal.rule} onClose={() => setModal(null)} onSaved={onSaved} />
                : null
            }
            {
                confirm ?
                    <FarConfirmModal open={true} danger={true} busy={busy} confirmText="Delete"
                        title={confirm.ids.length > 1 ? "Delete "+confirm.ids.length+" rules?" : "Delete rule?"}
                        message={(confirm.name ? "\""+confirm.name+"\" " : "The selected rules ")+"will be removed for the web portal and the Outlook plug-in. This cannot be undone."}
                        onClose={() => setConfirm(null)} onConfirm={() => runBulk(confirm.ids, "delete")} />
                : null
            }
        </div>
    );
}

FarRulesTab.defaultProps = { rules: [], loading: false, loadError: null, canManage: true, onRetry: () => {} };
FarRulesTab.propTypes = {
    rules: PropTypes.array,
    loading: PropTypes.bool,
    loadError: PropTypes.string,
    canManage: PropTypes.bool,
    onRulesChanged: PropTypes.func.isRequired,
    onRetry: PropTypes.func
};

export default FarRulesTab;
